import React, { useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { Button } from "../../../../shared/ui";

export interface AuthenticationRequiredModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (code: string) => void;
  isSubmitting?: boolean;
}

export const AuthenticationRequiredModal: React.FC<
  AuthenticationRequiredModalProps
> = ({ open, onClose, onConfirm, isSubmitting }) => {
  const [digits, setDigits] = useState<string[]>(["", "", "", "", "", ""]);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleDigitChange = (index: number, value: string) => {
    // Only accept numeric character
    const cleaned = value.replace(/\D/g, "");
    if (!cleaned) {
      const next = [...digits];
      next[index] = "";
      setDigits(next);
      return;
    }

    // If pasted multi-digit string
    if (cleaned.length > 1) {
      const pastedDigits = cleaned.slice(0, 6).split("");
      const next = [...digits];
      pastedDigits.forEach((d, idx) => {
        if (index + idx < 6) next[index + idx] = d;
      });
      setDigits(next);
      const focusIndex = Math.min(5, index + pastedDigits.length);
      inputRefs.current[focusIndex]?.focus();
      return;
    }

    const next = [...digits];
    next[index] = cleaned[0];
    setDigits(next);

    // Auto-advance
    if (index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const fullCode = digits.join("");
  const isComplete = fullCode.length === 6;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isComplete) {
      onConfirm(fullCode);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs transition-opacity" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-[480px] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-xl focus:outline-hidden">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex flex-col gap-1">
              <Dialog.Title className="text-[18px] font-semibold text-[#101828]">
                Authentication required
              </Dialog.Title>
              <Dialog.Description className="text-[13px] text-[#667085]">
                Please enter the 6-digit code from your authenticator app to continue.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-1 text-[#98A2B3] transition-colors hover:bg-[#F2F4F7] hover:text-[#344054]"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-6">
            {/* 6 Digit Input Boxes */}
            <div className="flex items-center justify-between gap-2 sm:gap-3">
              {digits.map((digit, idx) => (
                <input
                  key={idx}
                  ref={(el) => {
                    inputRefs.current[idx] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={digit}
                  onChange={(e) => handleDigitChange(idx, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(idx, e)}
                  className="h-[48px] w-[48px] rounded-[8px] border border-transparent bg-[#F8F9FC] text-center text-[18px] font-bold text-[#101828] focus:border-[#6F00FF] focus:bg-white focus:outline-hidden focus:ring-1 focus:ring-[#6F00FF]"
                />
              ))}
            </div>

            {/* Actions */}
            <div className="mt-4 flex items-center justify-end gap-3 border-t border-[#F2F4F7] pt-4">
              <button
                type="button"
                onClick={onClose}
                className="h-10 rounded-[8px] px-4 py-2 text-[14px] font-medium text-[#344054] transition-colors hover:text-[#101828]"
              >
                Back
              </button>
              <Button
                type="submit"
                disabled={!isComplete || isSubmitting}
                className="h-10 rounded-[8px] bg-[#020618] px-6 text-[14px] font-medium text-white shadow-xs transition-colors hover:bg-[#0f172a] disabled:bg-[#90A1B9] disabled:text-white disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Updating..." : "Update status"}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
